import { z } from 'zod';
import { buildFilterString } from '@/common/api/items';
import { REPORTING_STORE_API_BASE } from '@/common/api/reporting-store/constants';
import { tempoApi } from '@/common/api/tempoApi';
import type { ItemTreeSet } from '@/common/api/types/items';
import type { TimeLineDataSet } from '@/common/api/types/planner/timelineItem';
import {
  and,
  containsIgnoreCase,
  equals,
  greaterThan,
  greaterThanOrEqualTo,
  includes,
  isNotNull,
  isNull,
  lessThan,
  lessThanOrEqualTo,
  or,
} from '@/common/utils/rsqlFilters';
import type { ScenarioItemFilters } from '@/features/planner/api/adaptive-planner/scenarioItemFilters';

const itemTreeSetEntrySchema = z.object({
  id: z.number(),
  parentId: z.number().nullable(),
  matched: z.boolean(),
  itemTypeId: z.number(),
  hasExternalKey: z.boolean(),
  loopId: z.number().optional(),
  parentManagedByIntegration: z.boolean().optional().default(false),
  isDeletedInItemStore: z.boolean().optional(),
});

const itemTreeSetSchema = z.object({
  recordCount: z.number().nullable(),
  data: z.array(itemTreeSetEntrySchema),
});

type DatetimeFilterValue = ScenarioItemFilters['plannedStartDateTime'];

const buildDatetimeFilterRsql = (
  field: string,
  filter: DatetimeFilterValue,
): string | undefined => {
  if (!filter) return undefined;

  if (filter.operator === 'null') return isNull(field);
  if (filter.operator === 'notnull') return isNotNull(field);

  const values: string[] = Array.isArray(filter.value)
    ? filter.value
    : filter.value
      ? [filter.value]
      : [];
  if (values.length === 2 && (filter.operator === 'eq' || filter.operator === 'in')) {
    const [start, end] = values as [string, string];
    return `${greaterThanOrEqualTo(field, start)};${lessThanOrEqualTo(field, end)}`;
  }
  if (values.length !== 1) return undefined;

  const [value] = values as [string];
  if (filter.operator === 'gt') return greaterThan(field, value);
  if (filter.operator === 'lt') return lessThan(field, value);
  if (filter.operator === 'eq') return equals(field, value);
  return undefined;
};

export const buildScenarioItemFiltersRsql = (filters?: ScenarioItemFilters): string | undefined => {
  if (!filters) return undefined;

  const parts: string[] = [];

  if (filters.teamIds?.length && filters.noTeamSet) {
    parts.push(or(includes('teamId', filters.teamIds.map(String)), isNull('teamId')));
  } else if (filters.teamIds?.length) {
    parts.push(includes('teamId', filters.teamIds.map(String)));
  } else if (filters.noTeamSet) {
    parts.push(isNull('teamId'));
  }

  if (filters.estimationIds?.length && filters.noEstimateSet) {
    parts.push(
      or(includes('estimationId', filters.estimationIds.map(String)), isNull('estimationId')),
    );
  } else if (filters.estimationIds?.length) {
    parts.push(includes('estimationId', filters.estimationIds.map(String)));
  } else if (filters.noEstimateSet) {
    parts.push(isNull('estimationId'));
  }
  if (filters.healthLevels?.length) {
    parts.push(includes('healthLevel', filters.healthLevels));
  }
  if (filters.itemTypeIds?.length) {
    parts.push(includes('itemTypeId', filters.itemTypeIds.map(String)));
  }
  if (filters.itemStatusIds?.length) {
    parts.push(includes('itemStatusId', filters.itemStatusIds.map(String)));
  }
  if (filters.needsTeamOrEstimate) {
    parts.push(equals('needsTeamOrEstimate', 'true'));
  }
  if (filters.isMLImputed) {
    parts.push(equals('isMLImputed', 'true'));
  }
  if (filters.inDoneSubtree) {
    parts.push(equals('inDoneSubtree', 'true'));
  }
  if (filters.unparented) {
    parts.push(isNull('parentId'));
  }
  if (filters.objectiveIds?.length) {
    parts.push(includes('objectiveId', filters.objectiveIds.map(String)));
  }
  if (filters.hasObjective !== undefined) {
    parts.push(equals('hasObjective', String(filters.hasObjective)));
  }
  if (filters.hasDependencies) {
    parts.push(equals('hasDependencies', 'true'));
  }
  if (filters.hasPredecessor) {
    parts.push(equals('hasDependencies.predecessors', 'true'));
  }
  if (filters.hasSuccessor) {
    parts.push(equals('hasDependencies.successors', 'true'));
  }
  if (filters.hasEstimate) {
    parts.push(isNotNull('estimationId'));
  }
  if (filters.noEstimate) {
    parts.push(isNull('estimationId'));
  }
  const plannedStartDateTimeRsql = buildDatetimeFilterRsql(
    'plannedStartDateTime',
    filters.plannedStartDateTime,
  );
  if (plannedStartDateTimeRsql) parts.push(plannedStartDateTimeRsql);

  const plannedFinishDateTimeRsql = buildDatetimeFilterRsql(
    'plannedFinishDateTime',
    filters.plannedFinishDateTime,
  );
  if (plannedFinishDateTimeRsql) parts.push(plannedFinishDateTimeRsql);

  const forecastedStartDateTimeRsql = buildDatetimeFilterRsql(
    'forecastedStartDateTime',
    filters.forecastedStartDateTime,
  );
  if (forecastedStartDateTimeRsql) parts.push(forecastedStartDateTimeRsql);

  const forecastedFinishDateTimeRsql = buildDatetimeFilterRsql(
    'forecastedFinishDateTime',
    filters.forecastedFinishDateTime,
  );
  if (forecastedFinishDateTimeRsql) parts.push(forecastedFinishDateTimeRsql);

  const latestFinishDateTimeRsql = buildDatetimeFilterRsql(
    'latestFinishDateTime',
    filters.latestFinishDateTime,
  );
  if (latestFinishDateTimeRsql) parts.push(latestFinishDateTimeRsql);

  const deadlineDateTimeRsql = buildDatetimeFilterRsql(
    'deadlineDateTime',
    filters.deadlineDateTime,
  );
  if (deadlineDateTimeRsql) parts.push(deadlineDateTimeRsql);

  const createdAtRsql = buildDatetimeFilterRsql('createdAt', filters.createdAt);
  if (createdAtRsql) parts.push(createdAtRsql);

  const updatedAtRsql = buildDatetimeFilterRsql('updatedAt', filters.updatedAt);
  if (updatedAtRsql) parts.push(updatedAtRsql);

  const doneDateTimeRsql = buildDatetimeFilterRsql('doneDateTime', filters.doneDateTime);
  if (doneDateTimeRsql) parts.push(doneDateTimeRsql);
  if (filters.searchText?.trim()) {
    const words = filters.searchText.trim().split(/\s+/).filter(Boolean);
    const wordConditions = words.map((word) =>
      or(containsIgnoreCase('name', word), containsIgnoreCase('externalKey', word)),
    );
    parts.push(...wordConditions);
  }

  const customFieldFiltersRsql = buildFilterString(filters.customFieldFilters);
  if (customFieldFiltersRsql) parts.push(customFieldFiltersRsql);

  return parts.length > 0 ? and(...parts) : undefined;
};

export const adaptItemTreeSetToTimeLineDataSet = (treeSet: ItemTreeSet): TimeLineDataSet[] =>
  treeSet.data.map((entry) => ({
    itemId: entry.id,
    parentId: entry.parentId,
    matched: entry.matched,
    isDeletedInItemStore: entry.isDeletedInItemStore ?? false,
  }));

export const fetchReportingStorePlannerDataSet = async ({
  loopId,
  scenarioId,
  filters,
  order,
}: {
  loopId: number;
  scenarioId: number;
  filters?: ScenarioItemFilters;
  order?: string;
}): Promise<TimeLineDataSet[]> => {
  const filter = buildScenarioItemFiltersRsql(filters);
  const includeDescendants = (filters?.objectiveIds?.length ?? 0) > 0;
  const response = await tempoApi()
    .post(`${REPORTING_STORE_API_BASE}/loops/${loopId}/scenarios/${scenarioId}/items/data-set`, {
      json: { filter, order, includeDescendants },
    })
    .json();
  const treeSet: ItemTreeSet = itemTreeSetSchema.parse(response);
  return adaptItemTreeSetToTimeLineDataSet(treeSet);
};
